import { formatCalendarDate } from '../lib/format';
import type { CoverageGap } from '../lib/coverage-gaps';
import styles from './CoverageNotice.module.css';

export interface CoverageNoticeProps {
  /** The gap the map is currently centred in, or null when it isn't in one. */
  gap: CoverageGap | null;
  onDismiss: () => void;
}

/**
 * Says out loud what the scrim can only imply.
 *
 * Dimming an area communicates "something is off here" and nothing more, and
 * the areas in `lib/coverage-gaps.ts` need more than that: they are dimmed in
 * contradiction of the data source, which is a claim worth stating in words. A
 * viewer looking at their own town deserves to know that the emptiness is a
 * missing feed and where to go for the radar they actually want.
 *
 * Dismissable, because it is a standing condition rather than an event — nobody
 * needs telling twice in one sitting.
 */
export function CoverageNotice({ gap, onDismiss }: CoverageNoticeProps) {
  if (!gap) return null;

  return (
    <aside className={styles.notice} role="status">
      <p className={styles.body}>
        <strong className={styles.title}>No radar over {gap.name}.</strong> {gap.note}
      </p>

      {gap.localSource && (
        <p className={styles.body}>
          <a className={styles.link} href={gap.localSource.url} target="_blank" rel="noreferrer">
            {gap.localSource.label}
          </a>{' '}
          publishes its own imagery.
        </p>
      )}

      {/*
        Shown because this notice contradicts the data source on the strength of
        one manual check, and that check could be months old by the time anyone
        reads it. The app already tells the viewer its upstream is overstating;
        how fresh the correction is belongs in the same breath — and here it is
        in front of someone who might act on it, rather than in a comment.
      */}
      <p className={styles.checked}>Last checked {formatCalendarDate(gap.verifiedOn)}.</p>

      <button type="button" className={styles.dismiss} onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
    </aside>
  );
}
